import React, { useState, useEffect } from "react";
import {
  Carousel,
  Card,
  Col,
  Row,
  Button,
  Form,
  Modal,
  Dropdown,
  Alert,
} from "react-bootstrap";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";

export default function AnnouncementsPage() {
  const [announcements, setAnnouncements] = useState([]);
  const [latestAnnouncements, setLatestAnnouncements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    description: "",
    image: "",
    date: "",
  });
  const [role, setRole] = useState("");
  const [permissions, setPermissions] = useState({});

  // Fetch user role and permissions
  const fetchUserRoleAndPermissions = async () => {
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, "Users", user.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userRole = userDoc.data().role || "";
        setRole(userRole);

        const roleDocRef = doc(db, "Roles", userRole);
        const roleDoc = await getDoc(roleDocRef);
        if (roleDoc.exists()) {
          setPermissions(roleDoc.data().permissions || {});
        }
      }
    }
  };

  useEffect(() => {
    fetchUserRoleAndPermissions();
    fetchAnnouncements();
  }, []);

  // Fetch announcements from Firestore
  const fetchAnnouncements = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "announcements"));
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const parsedData = data.map((item) => ({
          ...item,
          date: item.date instanceof Timestamp ? item.date.toDate() : new Date(item.date),
        }));

        const sortedData = parsedData.sort((a, b) => b.date - a.date);

        setAnnouncements(sortedData);
        setLatestAnnouncements(sortedData.slice(0, 2));
      } else {
        setAnnouncements([]);
        setLatestAnnouncements([]);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  };

  // Add or Edit announcement in Firestore
  const handleSaveAnnouncement = async () => {
    if (newAnnouncement.title && newAnnouncement.description && newAnnouncement.date) {
      try {
        const announcementWithTimestamp = {
          ...newAnnouncement,
          date: Timestamp.fromDate(new Date(newAnnouncement.date)),
        };

        if (editMode) {
          const announcementRef = doc(db, "announcements", currentEditId);
          await updateDoc(announcementRef, announcementWithTimestamp);

          const updatedAnnouncements = announcements.map((item) =>
            item.id === currentEditId
              ? { ...item, ...announcementWithTimestamp, date: new Date(newAnnouncement.date) }
              : item
          );
          setAnnouncements(updatedAnnouncements);
          setLatestAnnouncements(updatedAnnouncements.slice(0, 2));
        } else {
          const docRef = await addDoc(collection(db, "announcements"), announcementWithTimestamp);

          const addedAnnouncement = {
            id: docRef.id,
            ...announcementWithTimestamp,
            date: new Date(newAnnouncement.date),
          };
          setAnnouncements([addedAnnouncement, ...announcements]);
          setLatestAnnouncements([addedAnnouncement, ...latestAnnouncements.slice(0, 1)]);
        }

        setNewAnnouncement({ title: "", description: "", image: "", date: "" });
        setShowModal(false);
        setEditMode(false);
        setCurrentEditId(null);
      } catch (error) {
        console.error("Error saving announcement:", error);
      }
    }
  };

  // Delete announcement from Firestore
  const handleDeleteAnnouncement = async (id) => {
    try {
      await deleteDoc(doc(db, "announcements", id));

      const updatedAnnouncements = announcements.filter((item) => item.id !== id);
      setAnnouncements(updatedAnnouncements);
      setLatestAnnouncements(updatedAnnouncements.slice(0, 2));
    } catch (error) {
      console.error("Error deleting announcement:", error);
    }
  };

  // Open modal for editing an announcement
  const handleEditAnnouncement = (announcement) => {
    setEditMode(true);
    setCurrentEditId(announcement.id);
    setNewAnnouncement({
      title: announcement.title,
      description: announcement.description,
      image: announcement.image,
      date: announcement.date.toISOString().split("T")[0],
    });
    setShowModal(true);
  };

  const canManageAnnouncements = role === "admin" || permissions.canMakeAnnouncements;

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* Enhanced Header Section */}
        <div className="text-center mb-5">
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="ri-megaphone-line fs-1 text-primary me-3"></i>
            <h1 className="display-6 fw-bold text-primary mb-0">Announcements</h1>
          </div>
          <p className="lead text-muted">Stay updated with the latest company news and announcements</p>
        </div>

        {/* Enhanced Recent Announcements Section */}
        <Card className="border-0 shadow-lg rounded-4 mb-5">
          <div className="bg-gradient-primary text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-newspaper-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">Recent Announcements</h5>
              </div>
              <span className="badge bg-white text-primary px-3 py-2 rounded-pill">
                {latestAnnouncements.length} featured
              </span>
            </div>
          </div>
          <Card.Body className="p-0">
            {latestAnnouncements.length > 0 ? (
              <Carousel slide className="rounded-bottom-4">
                {latestAnnouncements.map((announcement, index) => (
                  <Carousel.Item key={index}>
                    <div className="video-headline position-relative">
                      <img 
                        src={announcement.image || "https://via.placeholder.com/1200x400/667eea/ffffff?text=Announcement"} 
                        alt={announcement.title || "Announcement"}
                        className="w-100"
                        style={{ height: '400px', objectFit: 'cover' }}
                      />
                      <div className="position-absolute top-0 start-0 w-100 h-100" 
                           style={{ background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.8) 0%, rgba(118, 75, 162, 0.8) 100%)' }}>
                      </div>
                      <div className="video-headline-body p-4 position-absolute bottom-0 start-0 w-100 text-white">
                        <div>
                          <h1 className="video-title mb-3 fw-bold" style={{ fontSize: '2.5rem' }}>
                            {announcement.title}
                          </h1>
                          <p className="opacity-90 mb-4 fs-5" style={{ maxWidth: '600px' }}>
                            {announcement.description}
                          </p>
                          <div className="d-flex align-items-center">
                            <i className="ri-calendar-line me-2"></i>
                            <span className="opacity-75">
                              {announcement.date.toLocaleDateString('en-US', { 
                                weekday: 'long', 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric' 
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Carousel.Item>
                ))}
              </Carousel>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-newspaper-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Recent Announcements</h5>
                <p className="text-muted">Check back later for the latest updates.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Enhanced All Announcements Section */}
        <Card className="border-0 shadow-lg rounded-4">
          <div className="bg-gradient-success text-white p-4 rounded-top-4" 
               style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}>
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ri-file-list-3-line fs-4 me-3"></i>
                <h5 className="mb-0 fw-bold">All Announcements</h5>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className="badge bg-white text-success px-3 py-2 rounded-pill">
                  {announcements.length} total
                </span>
                {canManageAnnouncements && (
                  <Button 
                    variant="light" 
                    onClick={() => setShowModal(true)}
                    className="px-4 py-2 rounded-pill fw-semibold"
                  >
                    <i className="ri-add-line me-2"></i>
                    Create Announcement
                  </Button>
                )}
              </div>
            </div>
          </div>
          <Card.Body className="p-4">
            {announcements.length > 0 ? (
              <Row className="g-4">
                {announcements.map((announcement) => (
                  <Col key={announcement.id} lg={12}>
                    <Card className="border-0 shadow-sm rounded-4 h-100 position-relative"
                          style={{ 
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 15px 35px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                          }}>
                      {canManageAnnouncements && (
                        <Dropdown align="end" className="position-absolute top-0 end-0 m-3" style={{ zIndex: 10 }}>
                          <Dropdown.Toggle variant="light" className="btn btn-light rounded-circle p-2">
                            <i className="ri-more-2-fill"></i>
                          </Dropdown.Toggle>
                          <Dropdown.Menu className="shadow-lg border-0 rounded-3">
                            <Dropdown.Item 
                              onClick={() => handleEditAnnouncement(announcement)}
                              className="rounded-2 mx-2"
                            >
                              <i className="ri-edit-line me-2"></i>Edit
                            </Dropdown.Item>
                            <Dropdown.Item 
                              onClick={() => handleDeleteAnnouncement(announcement.id)}
                              className="rounded-2 mx-2 text-danger"
                            >
                              <i className="ri-delete-bin-line me-2"></i>Delete
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                      <Card.Body className="p-4">
                        <Row className="g-4 align-items-center">
                          <Col md={5}>
                            <div className="position-relative">
                              <img
                                src={announcement.image || "https://via.placeholder.com/600x300/667eea/ffffff?text=Announcement"}
                                className="img-fluid rounded-4 shadow-sm"
                                alt={announcement.title}
                                style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                              />
                              <div className="position-absolute top-0 start-0 m-3">
                                <span className="badge bg-primary rounded-pill px-3 py-2">
                                  <i className="ri-megaphone-line me-1"></i>
                                  Announcement
                                </span>
                              </div>
                            </div>
                          </Col>
                          <Col md={7}>
                            <div className="h-100 d-flex flex-column">
                              <Card.Title className="h4 fw-bold text-dark mb-3">
                                {announcement.title}
                              </Card.Title>
                              <div className="d-flex align-items-center mb-3">
                                <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                                  <i className="ri-calendar-line text-primary"></i>
                                </div>
                                <span className="text-muted fw-semibold">
                                  {announcement.date.toLocaleDateString('en-US', { 
                                    weekday: 'long', 
                                    year: 'numeric', 
                                    month: 'long', 
                                    day: 'numeric' 
                                  })}
                                </span>
                              </div>
                              <p className="text-muted mb-0 flex-grow-1">
                                {announcement.description}
                              </p>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="ri-file-list-3-line text-muted" style={{ fontSize: '4rem' }}></i>
                </div>
                <h5 className="text-muted mb-3">No Announcements Found</h5>
                <p className="text-muted">No announcements have been created yet.</p>
                {canManageAnnouncements && (
                  <Button 
                    variant="primary" 
                    onClick={() => setShowModal(true)}
                    className="mt-3 px-4 py-2 rounded-pill fw-semibold"
                  >
                    <i className="ri-add-line me-2"></i>
                    Create First Announcement
                  </Button>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>

      {/* Enhanced Create/Edit Announcement Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header className="bg-gradient-primary text-white border-0">
          <Modal.Title className="fw-bold">
            <i className="ri-megaphone-line me-2"></i>
            {editMode ? "Edit Announcement" : "Create Announcement"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                <i className="ri-text me-2"></i>Title
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter announcement title"
                value={newAnnouncement.title}
                onChange={(e) =>
                  setNewAnnouncement({ ...newAnnouncement, title: e.target.value })
                }
                className="form-control-lg shadow-sm border-2"
                style={{ borderRadius: '12px' }}
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                <i className="ri-file-text-line me-2"></i>Description
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Enter announcement details"
                value={newAnnouncement.description}
                onChange={(e) =>
                  setNewAnnouncement({ ...newAnnouncement, description: e.target.value })
                }
                className="shadow-sm border-2"
                style={{ borderRadius: '12px' }}
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                <i className="ri-image-line me-2"></i>Image URL (Optional)
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter image URL"
                value={newAnnouncement.image}
                onChange={(e) =>
                  setNewAnnouncement({ ...newAnnouncement, image: e.target.value })
                }
                className="form-control-lg shadow-sm border-2"
                style={{ borderRadius: '12px' }}
              />
              <Form.Text className="text-muted">
                <i className="ri-information-line me-1"></i>
                Provide a URL to an image that will be displayed with your announcement.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">
                <i className="ri-calendar-line me-2"></i>Date
              </Form.Label>
              <Form.Control
                type="date"
                value={newAnnouncement.date}
                onChange={(e) =>
                  setNewAnnouncement({ ...newAnnouncement, date: e.target.value })
                }
                className="form-control-lg shadow-sm border-2"
                style={{ borderRadius: '12px' }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 p-4">
          <Button variant="secondary" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-pill fw-semibold">
            <i className="ri-close-line me-2"></i>Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAnnouncement} className="px-4 py-2 rounded-pill fw-semibold">
            <i className="ri-save-line me-2"></i>
            {editMode ? "Save Changes" : "Create Announcement"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
